import React, { useEffect, useRef, useState, useCallback, createContext, useContext } from 'react';
import { Outlet, useNavigate, useLocation, NavLink, Navigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Drawer, IconButton, Typography, List, ListItemButton, AppBar, Toolbar, Collapse,
  useTheme, useMediaQuery, Menu, MenuItem, ListItemIcon, Divider, ButtonBase,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../contexts/AuthContext';
import api, { settingsApi, referralApi } from '../services/api';
import { tokens, marketplaceMeta } from '../theme';
import { MARKETPLACES, MP_SECTIONS, mpPath } from '../utils/marketplaces';
import { planLabel } from '../utils/format';
import { canTab, isMember } from '../utils/access';
import MarqitLogoFull, { MarqitLogoMark } from './MarqitLogoFull';
import LegalFooter from './LegalFooter';
import ConnectMarketplaceDialog from './ConnectMarketplaceDialog';

const RAIL = 68;
const WIDE = 288;
const OPEN_DELAY = 90;
const CLOSE_DELAY = 220;
const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

const SECTION_ICONS = {
  '': <InsightsRoundedIcon fontSize="small" />,
  reports: <DescriptionOutlinedIcon fontSize="small" />,
  costs: <Inventory2OutlinedIcon fontSize="small" />,
  stocks: <WarehouseOutlinedIcon fontSize="small" />,
  calculator: <CalculateOutlinedIcon fontSize="small" />,
  promotions: <LocalOfferOutlinedIcon fontSize="small" />,
  connections: <LinkRoundedIcon fontSize="small" />,
};

// Profile and connected accounts, shared with pages; openConnect() opens the connection dialog
// from anywhere (dashboard "+", sidebar, marketplace screens).
const ShellContext = createContext({ profile: null, accounts: [], loaded: false, reloadShell: () => {}, openConnect: () => {}, partner: 'none', reloadPartner: () => {} });
export const useShell = () => useContext(ShellContext);

// A marketplace as a small LED in its colour: lit when an account is connected, dim when not.
const MarketplaceDot = ({ mp, lit }) => {
  const c = marketplaceMeta(mp).main;
  return (
    <Box component="span" aria-hidden sx={{
      width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
      bgcolor: lit ? c : 'transparent', border: `2px solid ${c}`, opacity: lit ? 1 : 0.55,
      boxShadow: lit ? `0 0 0 3px ${c}1F, 0 0 8px 1px ${c}8C` : 'none',
    }} />
  );
};

// Label that fades with the rail instead of popping in and out, so the icon column never moves.
const Label = ({ show, children, sx }) => (
  <Box component="span" sx={{
    ml: 1.5, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left',
    opacity: show ? 1 : 0, transform: show ? 'none' : 'translateX(-6px)', transition: `opacity .18s ${EASE}, transform .22s ${EASE}`,
    ...sx,
  }}>
    {children}
  </Box>
);

// mx + px + half the icon slot = RAIL / 2: icons stay centred in the collapsed rail.
const itemSx = (active) => ({
  mx: 1, mb: 0.5, minHeight: 48, px: 1, borderRadius: '12px',
  color: active ? tokens.primaryDark : tokens.ink,
  bgcolor: active ? tokens.primarySoft : 'transparent',
  '&:hover': { bgcolor: active ? tokens.primarySoft : tokens.primaryTint },
});

const IconSlot = ({ children, active }) => (
  <Box sx={{ width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? tokens.primaryDark : tokens.inkSoft }}>
    {children}
  </Box>
);

const NavItem = ({ to, icon, label, expanded, onNavigate, end = false }) => {
  const location = useLocation();
  const active = end ? location.pathname === to : location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <ListItemButton component={NavLink} to={to} onClick={onNavigate} aria-current={active ? 'page' : undefined} sx={itemSx(active)}
      title={expanded ? undefined : label}>
      <IconSlot active={active}>{icon}</IconSlot>
      <Label show={expanded}><Typography component="span" sx={{ fontWeight: active ? 700 : 500, fontSize: '0.975rem' }}>{label}</Typography></Label>
    </ListItemButton>
  );
};

const SectionLabel = ({ expanded, children }) => (
  <Box sx={{ height: 36, px: 3.25, display: 'flex', alignItems: 'flex-end', pb: 0.75, position: 'relative' }}>
    <Typography variant="overline" sx={{ color: tokens.inkMuted, lineHeight: 1, opacity: expanded ? 1 : 0, transition: `opacity .18s ${EASE}`, whiteSpace: 'nowrap' }}>
      {children}
    </Typography>
    <Divider sx={{ position: 'absolute', left: 16, right: 16, bottom: 14, opacity: expanded ? 0 : 1, transition: `opacity .18s ${EASE}` }} />
  </Box>
);

// A signed-in account that may not use the service yet sees the whole menu, but only these
// sections work. Until the email is confirmed that is «Главная» and «Настройки»; once confirmed, but
// with no trial or tariff, also the tariffs, the partner cabinet and the guide.
const OPEN_PATHS = {
  email_unverified: ['/dashboard', '/settings'],
  tariff_required: ['/dashboard', '/tariffs', '/settings', '/partner', '/guide'],
};
const pathOpen = (restriction, pathname) =>
  !restriction || (OPEN_PATHS[restriction] || []).some((p) => pathname === p || pathname.startsWith(p + '/'));
const LOCK_HINT = {
  email_unverified: 'Откроется после подтверждения почты',
  tariff_required: 'Откроется после оплаты тарифа или бесплатного доступа',
};

const LockedItem = ({ icon, label, expanded, hint }) => (
  <Box title={hint}>
    <ListItemButton disabled aria-disabled="true" sx={{ ...itemSx(false), width: 'auto' }}>
      <IconSlot>{icon}</IconSlot>
      <Label show={expanded}><Typography component="span" sx={{ fontWeight: 500, fontSize: '0.975rem' }}>{label}</Typography></Label>
      <LockOutlinedIcon fontSize="small" sx={{ opacity: expanded ? 1 : 0, transition: `opacity .18s ${EASE}` }} />
    </ListItemButton>
  </Box>
);

// One marketplace with its submenu. Clicking the row opens the marketplace screen and unfolds the
// submenu; clicking it again while on that marketplace folds the submenu back.
const MarketplaceNav = ({ mp, connected, expanded, open, onToggle, onNavigate, onConnect, profile }) => {
  const location = useLocation();
  const base = mpPath(mp);
  const inside = location.pathname === base || location.pathname.startsWith(base + '/');
  const meta = marketplaceMeta(mp);
  const navigate = useNavigate();
  return (
    <Box>
      <ListItemButton
        onClick={() => {
          if (inside) {
            onToggle(mp, !open);
            return;
          }
          onToggle(mp, true);
          navigate(base);
          onNavigate();
        }}
        aria-expanded={open}
        aria-current={inside ? 'page' : undefined}
        sx={itemSx(inside && (!open || !expanded))}
        title={expanded ? undefined : meta.label}
      >
        <IconSlot active={inside}>
          <MarketplaceDot mp={mp} lit={connected} />
        </IconSlot>
        <Label show={expanded}>
          <Typography component="span" sx={{ fontWeight: inside ? 700 : 500, fontSize: '0.975rem' }}>{meta.label}</Typography>
          {!connected && <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', color: tokens.inkMuted, lineHeight: 1.2 }}>не подключён</Typography>}
        </Label>
        <Box
          component="span"
          role="button"
          tabIndex={-1}
          aria-label={open ? 'Свернуть' : 'Развернуть'}
          onClick={(e) => { e.stopPropagation(); onToggle(mp, !open); }}
          sx={{ display: 'flex', color: tokens.inkMuted, opacity: expanded ? 1 : 0, transition: `opacity .18s ${EASE}` }}
        >
          <ExpandMoreRoundedIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: `transform .25s ${EASE}` }} />
        </Box>
      </ListItemButton>
      <Collapse in={open && expanded} timeout={240} unmountOnExit>
        <Box sx={{ position: 'relative', ml: '34px', mr: 1, mb: 1, pl: 1, borderLeft: `2px solid ${tokens.border}` }}>
          {connected ? MP_SECTIONS.filter((s) => canTab(profile, s.key)).map((s) => {
            const to = mpPath(mp, s.key);
            const active = s.key ? location.pathname === to || location.pathname.startsWith(to + '/') : location.pathname === to;
            return (
              <ListItemButton key={s.key || 'overview'} component={NavLink} to={to} onClick={onNavigate} aria-current={active ? 'page' : undefined}
                sx={{ minHeight: 36, px: 1.25, py: 0.5, mb: 0.25, borderRadius: '10px', gap: 1.25,
                  color: active ? tokens.primaryDark : tokens.inkSoft, bgcolor: active ? tokens.primarySoft : 'transparent',
                  '&:hover': { bgcolor: active ? tokens.primarySoft : tokens.primaryTint, color: tokens.ink } }}>
                {SECTION_ICONS[s.key]}
                <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: active ? 700 : 500, whiteSpace: 'nowrap' }}>{s.label}</Typography>
              </ListItemButton>
            );
          }) : isMember(profile) ? (
            <Typography sx={{ px: 1.25, py: 0.75, fontSize: '0.85rem', color: tokens.inkMuted }}>Не подключён</Typography>
          ) : (
            <ListItemButton onClick={() => onConnect(mp)}
              sx={{ minHeight: 38, px: 1.25, borderRadius: '10px', gap: 1.25, color: tokens.primaryDark, fontWeight: 600, '&:hover': { bgcolor: tokens.primaryTint } }}>
              <AddRoundedIcon fontSize="small" />
              <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Подключить</Typography>
            </ListItemButton>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [partner, setPartner] = useState('none'); // the partner program: none | pending | active | blocked | rejected
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openMps, setOpenMps] = useState({});
  const [connect, setConnect] = useState(null); // { marketplace, account, onDone }
  const timer = useRef(null);
  const paperRef = useRef(null);

  const reloadShell = useCallback(async () => {
    try {
      const me = await settingsApi.me();
      setProfile(me.data);
      if (me.data?.restriction) {
        setAccounts([]); // the data is closed until the restriction is lifted
      } else {
        const accs = await api.get('/marketplace/accounts');
        setAccounts(accs.data || []);
      }
    } catch (e) {
      // the shell still works without these; pages show their own errors
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { reloadShell(); }, [reloadShell]);

  const reloadPartner = useCallback(() => {
    referralApi.status().then((r) => setPartner(r.data?.status || 'none')).catch(() => {});
  }, []);
  useEffect(() => {
    if (profile && profile.restriction !== 'email_unverified' && !isMember(profile)) reloadPartner();
  }, [profile, reloadPartner]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const schedule = (open, delay) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHoverOpen(open), delay);
  };
  const onEnter = () => schedule(true, OPEN_DELAY);
  const onLeave = () => { if (!menuAnchor) schedule(false, CLOSE_DELAY); };
  const onFocusIn = () => schedule(true, 0);
  const onFocusOut = (e) => {
    if (!menuAnchor && paperRef.current && !paperRef.current.contains(e.relatedTarget)) schedule(false, CLOSE_DELAY);
  };

  const expanded = isMobile || hoverOpen || Boolean(menuAnchor);
  // a closed panel forgets unfolded submenus: it always opens in its initial state
  const panelOpen = isMobile ? mobileOpen : expanded;
  useEffect(() => {
    if (!panelOpen) setOpenMps({});
  }, [panelOpen]);
  const closeMobile = () => isMobile && setMobileOpen(false);
  const toggleMp = (mp, open) => setOpenMps((s) => ({ ...s, [mp]: open }));

  const openConnect = useCallback((marketplace, account = null, onDone) => {
    setMobileOpen(false);
    setConnect({ marketplace, account, onDone });
  }, []);

  const finishConnect = async (result) => {
    const c = connect;
    setConnect(null);
    if (!result) return;
    await reloadShell();
    c?.onDone?.(result);
    // a new account starts loading its history on the server right away
    if (result.created && !c?.onDone) navigate(`/marketplaces/sync/${result.created.id}`);
  };

  const connected = new Set(accounts.map((a) => a.marketplace));
  const member = isMember(profile);
  const accessLabel = member
    ? `${profile.team.role_label || 'Сотрудник'}${profile.team.owner ? ` · ${profile.team.owner.full_name || profile.team.owner.email}` : ''}`
    : planLabel(profile);
  const restriction = profile?.restriction || ''; // '' | email_unverified | tariff_required
  const lockHint = LOCK_HINT[restriction];
  const open = (path) => pathOpen(restriction, path);
  const displayName = profile?.full_name || (user?.email ? user.email.split('@')[0] : 'Профиль');

  const drawer = (
    <Box ref={paperRef} onMouseEnter={isMobile ? undefined : onEnter} onMouseLeave={isMobile ? undefined : onLeave}
      onFocus={isMobile ? undefined : onFocusIn} onBlur={isMobile ? undefined : onFocusOut}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: tokens.paper, width: '100%' }}>
      <Box sx={{ height: 88, flexShrink: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
        <ButtonBase component={NavLink} to="/dashboard" onClick={closeMobile} aria-label="MARQIT Analytics — на главную"
          sx={{ position: 'absolute', inset: '16px 12px', borderRadius: '12px', justifyContent: 'flex-start', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', display: 'flex', opacity: expanded ? 0 : 1, transition: `opacity .18s ${EASE}` }}>
            <MarqitLogoMark width={36} />
          </Box>
          <Box sx={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', display: 'flex', opacity: expanded ? 1 : 0, transition: `opacity .22s ${EASE}` }}>
            <MarqitLogoFull width={168} />
          </Box>
        </ButtonBase>
      </Box>

      <Box component="nav" aria-label="Основное меню" sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 2 }}>
        <List disablePadding>
          <NavItem to="/dashboard" icon={<HomeRoundedIcon />} label="Главная" expanded={expanded} onNavigate={closeMobile} end />
        </List>

        <SectionLabel expanded={expanded}>Маркетплейсы</SectionLabel>
        <List disablePadding>
          {MARKETPLACES.map((mp) => (restriction ? (
            <LockedItem key={mp} icon={<MarketplaceDot mp={mp} lit={false} />} label={marketplaceMeta(mp).label} expanded={expanded} hint={lockHint} />
          ) : (
            <MarketplaceNav key={mp} mp={mp} connected={connected.has(mp)} expanded={expanded} open={Boolean(openMps[mp])}
              onToggle={toggleMp} onNavigate={closeMobile} onConnect={(m) => openConnect(m)} profile={profile} />
          )))}
        </List>

        <SectionLabel expanded={expanded}>Сервис</SectionLabel>
        <List disablePadding>
          {!member && (open('/tariffs')
            ? <NavItem to="/tariffs" icon={<CreditCardOutlinedIcon />} label="Тарифы" expanded={expanded} onNavigate={closeMobile} />
            : <LockedItem icon={<CreditCardOutlinedIcon />} label="Тарифы" expanded={expanded} hint={lockHint} />)}
          {!member && (restriction
            ? <LockedItem icon={<GroupsOutlinedIcon />} label="Команда" expanded={expanded} hint={lockHint} />
            : <NavItem to="/team" icon={<GroupsOutlinedIcon />} label="Команда" expanded={expanded} onNavigate={closeMobile} />)}
          <NavItem to="/settings" icon={<SettingsOutlinedIcon />} label="Настройки" expanded={expanded} onNavigate={closeMobile} />
          {open('/guide')
            ? <NavItem to="/guide" icon={<HelpOutlineRoundedIcon />} label="Как пользоваться" expanded={expanded} onNavigate={closeMobile} />
            : <LockedItem icon={<HelpOutlineRoundedIcon />} label="Как пользоваться" expanded={expanded} hint={lockHint} />}
          {!member && partner !== 'none' && <NavItem to="/partner" icon={<HandshakeOutlinedIcon />} label="Партнёрам" expanded={expanded} onNavigate={closeMobile} />}
          {isAdmin && <NavItem to="/admin/users" icon={<PeopleAltOutlinedIcon />} label="Пользователи" expanded={expanded} onNavigate={closeMobile} />}
          {isAdmin && <NavItem to="/admin/billing" icon={<ReceiptLongOutlinedIcon />} label="Заявки и промокоды" expanded={expanded} onNavigate={closeMobile} />}
          {isAdmin && <NavItem to="/admin/partners" icon={<HandshakeOutlinedIcon />} label="Партнёры" expanded={expanded} onNavigate={closeMobile} />}
          {isAdmin && <NavItem to="/admin/wb-news" icon={<NewspaperOutlinedIcon />} label="Мониторинг WB" expanded={expanded} onNavigate={closeMobile} />}
        </List>
      </Box>

      <Box sx={{ p: 1, flexShrink: 0 }}>
        <ButtonBase
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-haspopup="menu"
          aria-label="Мой профиль"
          sx={{ width: '100%', display: 'flex', alignItems: 'center', p: 0.75, borderRadius: '12px', bgcolor: tokens.profile, color: '#fff', textAlign: 'left', justifyContent: 'flex-start', overflow: 'hidden' }}
        >
          <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#7FE3DC', color: tokens.profile, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
            {displayName.slice(0, 1).toUpperCase()}
          </Box>
          <Label show={expanded}>
            <Typography component="span" sx={{ display: 'block', fontWeight: 600, fontSize: '0.975rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</Typography>
            <Typography component="span" sx={{ display: 'block', fontSize: '0.8rem', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' }}>{accessLabel || 'Мой профиль'}</Typography>
          </Label>
        </ButtonBase>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); schedule(false, CLOSE_DELAY); }}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">{displayName}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">{profile?.email || user?.email}</Typography>
            <Typography variant="caption" sx={{ color: tokens.primaryDark, fontWeight: 600 }}>{accessLabel}</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setMenuAnchor(null); closeMobile(); navigate('/settings'); }}>
            <ListItemIcon><PersonOutlineRoundedIcon fontSize="small" /></ListItemIcon>Профиль и пароль
          </MenuItem>
          {!member && (
            <MenuItem onClick={() => { setMenuAnchor(null); closeMobile(); navigate('/tariffs'); }}>
              <ListItemIcon><CreditCardOutlinedIcon fontSize="small" /></ListItemIcon>Мой тариф
            </MenuItem>
          )}
          <MenuItem onClick={() => { setMenuAnchor(null); logout(); navigate('/login'); }} sx={{ color: tokens.error }}>
            <ListItemIcon><LogoutRoundedIcon fontSize="small" sx={{ color: tokens.error }} /></ListItemIcon>Выйти
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  if (!loaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress aria-label="Загрузка" /></Box>
    );
  }
  const shutOut = !open(location.pathname);

  return (
    <ShellContext.Provider value={{ profile, accounts, loaded, reloadShell, openConnect, partner, reloadPartner }}>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: tokens.bg }}>
        {isMobile && (
          <AppBar position="fixed" elevation={0} sx={{ bgcolor: tokens.paper, color: tokens.ink, borderBottom: `1px solid ${tokens.border}` }}>
            <Toolbar sx={{ minHeight: 60, gap: 1 }}>
              <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Открыть меню">
                <MenuRoundedIcon />
              </IconButton>
              <Box component={NavLink} to="/dashboard" aria-label="На главную" sx={{ display: 'flex' }}>
                <MarqitLogoFull width={128} />
              </Box>
            </Toolbar>
          </AppBar>
        )}

        {isMobile ? (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: { width: WIDE, maxWidth: '86vw', border: 'none' } }}>
            {drawer}
          </Drawer>
        ) : (
          <>
            {/* the rail always takes RAIL px; the open panel slides over the page instead of pushing it */}
            <Box component="aside" sx={{ width: RAIL, flexShrink: 0 }} />
            <Box sx={{
              position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: theme.zIndex.drawer, width: expanded ? WIDE : RAIL,
              borderRight: `1px solid ${tokens.border}`, bgcolor: tokens.paper, overflow: 'hidden',
              boxShadow: expanded ? '8px 0 32px rgba(15, 31, 30, 0.10)' : 'none',
              transition: `width .28s ${EASE}, box-shadow .28s ${EASE}`,
            }}>
              <Box sx={{ width: WIDE, height: '100%' }}>{drawer}</Box>
            </Box>
          </>
        )}

        <Box component="main" id="main" sx={{ flexGrow: 1, minWidth: 0, px: { xs: 2, sm: 3, lg: 5 }, pt: { xs: 10, md: 4 }, pb: 6 }}>
          {restriction === 'email_unverified' && (
            <Alert severity="warning" sx={{ mb: 2.5, maxWidth: 1480, mx: 'auto' }}
              action={location.pathname.startsWith('/settings') ? undefined : (
                <Button color="inherit" size="small" onClick={() => navigate('/settings#email-confirm')}>Подтвердить</Button>
              )}>
              Подтвердите почту в разделе «Настройки»: пока этого не сделано, работают только «Главная» и «Настройки».
            </Alert>
          )}
          {restriction === 'tariff_required' && (
            <Alert severity="info" sx={{ mb: 2.5, maxWidth: 1480, mx: 'auto' }}
              action={location.pathname.startsWith('/tariffs') ? undefined : (
                <Button color="inherit" size="small" onClick={() => navigate('/tariffs')}>Тарифы</Button>
              )}>
              Сервис откроется после оплаты тарифа или бесплатного доступа. Получить его можно в разделе «Тарифы».
            </Alert>
          )}
          {shutOut ? <Navigate to="/dashboard" replace /> : <Outlet />}
          <LegalFooter sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(17,24,39,0.06)' }} />
        </Box>
      </Box>
      <ConnectMarketplaceDialog open={Boolean(connect)} marketplace={connect?.marketplace} account={connect?.account} onClose={finishConnect} />
    </ShellContext.Provider>
  );
};

export default Layout;
